import React from "react";
import { motion } from "framer-motion";
import { THEME_META } from "../../lib/catalog";
import { Globe2, Landmark, Brain, ScrollText, Newspaper, TrendingUp, ArrowRight } from "lucide-react";

const VISUAL = {
  "geopolitics-middle-east": {
    Icon: Globe2,
    desc: "Iran, Israel, energy corridors and the great-power chessboard.",
    img: "https://static.prod-images.emergentagent.com/jobs/e8b99965-4b11-4e8e-884d-05f09cb42e4c/images/e7d707acc96793f1f3000007449886902edd05bf43e5465a0e61a7c2ff69167a.jpeg",
  },
  "political-islam": {
    Icon: Landmark,
    desc: "The Muslim Brotherhood, Sharia debates and Europe's internal frontier.",
    img: "https://static.prod-images.emergentagent.com/jobs/e8b99965-4b11-4e8e-884d-05f09cb42e4c/images/2cd6454adb14b805c6e94c698858feea53821027ea0bb46e8e7a01b526ad36d5.jpeg",
  },
  "moral-psychology": {
    Icon: Brain,
    desc: "Cognitive bias, identity and the moral wiring beneath politics.",
    img: "https://static.prod-images.emergentagent.com/jobs/e8b99965-4b11-4e8e-884d-05f09cb42e4c/images/a8b2b87036f3e1e8ddcb79e26f2ebccff256129d6a713c02e65253809b5a3eb8.jpeg",
  },
  "mythology-history": {
    Icon: ScrollText,
    desc: "Temples, empires and the long arc of civilizational memory.",
    img: "https://static.prod-images.emergentagent.com/jobs/e8b99965-4b11-4e8e-884d-05f09cb42e4c/images/c15d3c3d206bc31cb54cb996a2bde084d7d54d4177eae413db8003a02bb4c197.jpeg",
  },
  "media-disinformation": {
    Icon: Newspaper,
    desc: "Propaganda, narrative warfare and the fight over truth.",
    img: "https://static.prod-images.emergentagent.com/jobs/e8b99965-4b11-4e8e-884d-05f09cb42e4c/images/cc1fdcae2eafea8ccdf04b0c6774436c555c32d5a62fa1b6ab08ad8b2db1cf7b.jpeg",
  },
  "business-markets": {
    Icon: TrendingUp,
    desc: "M&A, capital and the economics that move nations.",
    img: "https://static.prod-images.emergentagent.com/jobs/e8b99965-4b11-4e8e-884d-05f09cb42e4c/images/8626d878510e626b90f04569627964d5d259bbec4d38b18e58c84cdefdca09a3.jpeg",
  },
};

export default function ThemesGallery({ themes = [], onPick }) {
  if (!themes.length) return null;
  return (
    <section id="themes" className="scroll-mt-20 relative max-w-[1320px] mx-auto px-5 sm:px-8 py-14 sm:py-16">
      <div className="flex items-end justify-between mb-8 border-b border-[var(--line)] pb-4">
        <div>
          <p className="meta-label text-[var(--gold)]">Disciplines</p>
          <h2 className="font-serif text-3xl sm:text-4xl mt-2 text-[var(--txt)]">Browse by Theme</h2>
        </div>
        <span className="meta-label text-[var(--muted)] hidden sm:block">Hover / tap a card</span>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {themes.map((t, i) => {
          const v = VISUAL[t.id] || VISUAL["geopolitics-middle-east"];
          const Icon = v.Icon;
          const tc = THEME_META[t.id]?.color || "var(--gold)";
          return (
            <motion.div
              key={t.id}
              data-testid={`theme-card-${t.id}`}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => onPick(t.id)}
              className="group h-52 cursor-pointer"
              style={{ perspective: "1200px" }}
            >
              <div
                className="relative w-full h-full transition-transform duration-[650ms] group-hover:[transform:rotateY(180deg)]"
                style={{ transformStyle: "preserve-3d", transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}
              >
                {/* Front */}
                <div
                  className="on-dark absolute inset-0 flex flex-col justify-between p-6 bg-[var(--surface)] border border-[var(--line)] group-hover:border-[var(--gold)] overflow-hidden"
                  style={{ backfaceVisibility: "hidden" }}
                >
                  <div
                    className="absolute inset-0 bg-center bg-cover transition-transform duration-[900ms] group-hover:scale-105"
                    style={{ backgroundImage: `url(${v.img})` }}
                  />
                  <div
                    className="absolute inset-0"
                    style={{ background: "linear-gradient(180deg, rgba(11,13,15,0.45) 0%, rgba(11,13,15,0.7) 55%, rgba(11,13,15,0.95) 100%)" }}
                  />
                  <div className="relative flex items-start justify-between">
                    <Icon size={30} className="drop-shadow" style={{ color: tc }} strokeWidth={1.4} />
                    <span className="meta-label text-[0.58rem]" style={{ color: tc }}>{t.code}</span>
                  </div>
                  <span className="absolute bottom-0 left-0 h-[3px] w-full" style={{ backgroundColor: tc }} />
                  <div className="relative">
                    <h3 className="font-serif text-[1.7rem] leading-[1.05] text-white drop-shadow">{t.label}</h3>
                    <p className="meta-label text-[var(--gold-bright)] text-[0.58rem] mt-2">{t.count} dossiers</p>
                  </div>
                </div>

                {/* Back */}
                <div
                  className="absolute inset-0 flex flex-col justify-between p-6 bg-[var(--surface-2)] border"
                  style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)", borderColor: tc }}
                >
                  <div>
                    <span className="meta-label text-[0.58rem]" style={{ color: tc }}>{t.code}</span>
                    <p className="mt-3 text-[0.95rem] text-[var(--txt-2)] leading-relaxed font-serif italic">{v.desc}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="meta-label text-[var(--muted)] text-[0.56rem]">{t.count} dossiers</span>
                    <span className="flex items-center gap-1.5 meta-label text-[0.58rem] text-[var(--gold)]">
                      Browse <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
